import React from "react";
import {Box, Chip, Grid, Paper, Stack, Typography} from "@mui/material";
import {Autorenew, EventBusy} from "@mui/icons-material";
import type {Supplier360} from "../../supplier360DemoData";
import {contractStatusColor, formatCurrency, formatDate} from "../../supplier360Utils";

type Props = { supplier: Supplier360 };

const statusLabel: Record<string, string> = {
    active: 'Active',
    expiring: 'Expiring soon',
    expired: 'Expired',
    draft: 'Draft',
};

export const ContractsTab: React.FC<Props> = ({supplier}) => {
    if (supplier.contracts.length === 0) {
        return <Typography color="text.secondary">No contracts on record for this supplier.</Typography>;
    }

    return <Grid container spacing={2}>
        {supplier.contracts.map(c => (
            <Grid size={{xs: 12, md: 6}} key={c.id}>
                <Paper variant="outlined" sx={{p: 2, borderRadius: '10px', height: '100%'}}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Box>
                            <Typography variant="h3" sx={{fontSize: 16}}>{c.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.type}</Typography>
                        </Box>
                        <Chip size="small" color={contractStatusColor(c.status)} label={statusLabel[c.status]}/>
                    </Box>

                    <Stack direction="row" spacing={3} mt={2}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Value</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {c.value > 0 ? formatCurrency(c.value, supplier.currency) : '—'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Term</Typography>
                            <Typography variant="body2">
                                {formatDate(c.startDate)} → {formatDate(c.endDate)}
                            </Typography>
                        </Box>
                    </Stack>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                        <Typography variant="caption" color="text.secondary">Owner: {c.owner}</Typography>
                        <Chip
                            size="small"
                            variant="outlined"
                            icon={c.autoRenew ? <Autorenew/> : <EventBusy/>}
                            label={c.autoRenew ? 'Auto-renew' : 'Manual renewal'}
                            color={c.autoRenew ? 'success' : 'default'}
                        />
                    </Box>
                </Paper>
            </Grid>
        ))}
    </Grid>;
};
